import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { Order } from "@shared/types";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/Button";
import { CheckCircleIcon } from "@/components/icons";
import { whatsappLink } from "@/lib/whatsapp";

const PAYMENT_LABEL: Record<Order["paymentStatus"], string> = {
  unpaid: "Payment pending",
  deposit_paid: "Deposit received",
  paid: "Paid in full",
  skipped: "To be arranged on delivery",
};

export function OrderConfirmation() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (!id) return;
    api.orders.get(id).then(setOrder).catch(() => setOrder(null));
  }, [id]);

  const cancelled = searchParams.get("status") === "cancelled";

  if (!order) {
    return <div className="mx-auto min-h-screen max-w-app animate-pulse bg-cream" />;
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-app flex-col items-center gap-6 bg-cream px-6 py-16 text-center sm:my-6 sm:min-h-[calc(100vh-3rem)] sm:rounded-3xl sm:border sm:border-stone-300 sm:shadow-raised">
      <CheckCircleIcon className="text-camel" />
      <div>
        <h1 className="font-display text-2xl font-medium text-ink">
          {cancelled ? "Order placed — payment not completed" : "Thank you for your order"}
        </h1>
        <p className="mt-2 text-sm text-stone-700">
          Order <span className="font-semibold text-ink">{order.orderNumber}</span>
        </p>
      </div>

      {cancelled && (
        <p className="rounded-lg bg-stone-100 px-4 py-3 text-xs leading-relaxed text-stone-700">
          Your payment didn't go through, but your order was saved. We'll reach out to arrange payment — you can also
          contact us directly.
        </p>
      )}

      <div className="w-full rounded-xl border border-stone-300 bg-white p-4 text-left">
        {order.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-1.5 text-sm">
            <span className="text-ink">
              {item.qty} × {item.productName}
            </span>
            <span className="text-stone-700">{formatMoney(item.unitPriceCents * item.qty, order.currency)}</span>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-between border-t border-stone-300 pt-2 text-sm font-semibold">
          <span className="text-ink">Subtotal</span>
          <span className="text-ink">{formatMoney(order.subtotalCents, order.currency)}</span>
        </div>
        {order.requiresDeposit && (
          <div className="flex items-center justify-between pt-1 text-sm">
            <span className="text-stone-700">Deposit</span>
            <span className="text-stone-700">{formatMoney(order.depositAmountCents, order.currency)}</span>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between border-t border-stone-300 pt-2 text-xs">
          <span className="text-stone-500">Payment status</span>
          <span className="font-semibold text-ink">{PAYMENT_LABEL[order.paymentStatus]}</span>
        </div>
      </div>

      <a
        href={whatsappLink(`Hi Allthingsnew! Question about my order ${order.orderNumber}.`)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs font-semibold text-camel-dark underline"
      >
        Questions about your order? Chat with us on WhatsApp
      </a>

      <Link to="/" className="w-full">
        <Button fullWidth>Continue Shopping</Button>
      </Link>
    </div>
  );
}
